// Master Apps · Download de documentos pelo SERVIDOR (só admin).
//
// O navegador nunca vê as credenciais das fontes (PAT do GitHub, client
// credentials do Graph): quem baixa é a API. Este módulo tem só duas peças:
//  - `docDownloadSource(url)` — espelho da regra do servidor para o front saber
//    QUANDO mostrar o botão de download (github.com / *.sharepoint.com);
//  - `downloadDocument(appId, field)` — chama a rota admin, recebe o binário e
//    dispara o "salvar como" com o nome real do arquivo (Content-Disposition).
//
// A barreira de verdade é do servidor (`requireRole('admin')` + URL lida do
// banco); esconder o botão para colaborador é só cortesia de UI.
// Relativo (e não `@/lib/compliance`) de propósito: este lib também compila no
// tsconfig do apps/api (testes), onde só os aliases com shim em
// tests/web-*-alias.d.ts resolvem — o relativo dispensa mais um shim.
import { getClerk } from '@/store/session';
import type { DocKey } from './compliance';

export type DocDownloadSource = 'github' | 'sharepoint';

/** Espelho de `documentSource` da API — decide se o botão de baixar aparece. */
export function docDownloadSource(url: string | null | undefined): DocDownloadSource | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  // https-only, como no servidor (as fontes só existem em https).
  if (parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  if (host === 'github.com' || host === 'www.github.com') {
    // Só as FORMAS com download direto (raiz do repo, tree, blob, raw): link de
    // wiki/issues/releases mostraria um botão que sempre falha com 400.
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length < 2) return null;
    if (parts.length === 2) return 'github';
    return parts[2] === 'blob' || parts[2] === 'raw' || parts[2] === 'tree' ? 'github' : null;
  }
  if (host.endsWith('.sharepoint.com')) return 'sharepoint';
  return null;
}

/** Campo da API correspondente a cada artefato documental da UI. */
export const DOC_FIELD_BY_KEY: Record<DocKey, string> = {
  req: 'requirements',
  tech: 'docsTechnical',
  biz: 'docsBusiness',
  api: 'docsApi',
  repo: 'repo',
};

/** Nome do arquivo vindo do `Content-Disposition` (preferindo o filename* UTF-8). */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // %-encoding quebrado no header: cai para o filename simples abaixo.
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain?.[1] ?? null;
}

/**
 * Baixa o documento de um campo da aplicação e dispara o save do navegador.
 * Lança `Error` com mensagem pronta para toast quando a API recusa.
 */
export async function downloadDocument(applicationId: string, field: string): Promise<void> {
  const headers = new Headers();
  try {
    const token = await getClerk()?.session?.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // sem token (modo dev) — a API decide
  }

  const res = await fetch(
    `/api/documents/${encodeURIComponent(applicationId)}/${encodeURIComponent(field)}/download`,
    { headers, credentials: 'include' },
  );
  if (!res.ok) {
    let message = `Falha ao baixar o documento (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // resposta sem JSON: fica a mensagem genérica com o status
    }
    throw new Error(message);
  }

  // A rota real SEMPRE manda Content-Disposition. Um 200 sem ele é outra coisa
  // no caminho (portal de SSO, proxy corporativo devolvendo HTML de login) —
  // salvar isso como "documento" seria um arquivo lixo sem nenhum aviso.
  const disposition = res.headers.get('content-disposition');
  if (!disposition) {
    throw new Error('Resposta inesperada do servidor — sessão expirada ou proxy no caminho');
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(disposition) ?? 'documento';
  const objectUrl = URL.createObjectURL(blob);
  try {
    // `document` via globalThis + tipo estrutural mínimo, e não o símbolo
    // global: este lib roda nos testes do apps/api (tsconfig SEM lib dom, onde
    // outro arquivo declara um `document` dublê próprio) — referenciar o global
    // amarraria o tipo daqui ao dublê de lá.
    const doc = (
      globalThis as {
        document?: {
          createElement(tag: 'a'): {
            href: string;
            download: string;
            click(): void;
            remove(): void;
          };
          body: { appendChild(node: unknown): void };
        };
      }
    ).document;
    if (!doc) throw new Error('O download precisa rodar no navegador');
    const anchor = doc.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    doc.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // O revoke imediato é seguro: o click síncrono já iniciou o download.
    URL.revokeObjectURL(objectUrl);
  }
}
